/**
 * Environment Configuration & Validation
 * Validates required environment variables at startup and provides typed access
 */

// Required environment variables
const REQUIRED_ENV_VARS = [
  'REACT_APP_AUTH0_DOMAIN',
  'REACT_APP_AUTH0_CLIENT_ID',
  'REACT_APP_AUTH0_AUDIENCE',
  'REACT_APP_SERVER_DOMAIN',
];

// Optional environment variables with defaults
const OPTIONAL_ENV_VARS = {
  REACT_APP_ENV: 'production',
};

/**
 * Validates that all required environment variables are set
 * @throws {Error} if any required environment variable is missing
 */
export const validateEnv = () => {
  const missing = REQUIRED_ENV_VARS.filter(
    (varName) => !process.env[varName]
  );

  if (missing.length > 0) {
    const errorMessage = `Missing required environment variables:\n${missing
      .map((v) => `  - ${v}`)
      .join('\n')}`;

    // In development, show a warning but don't crash
    if (process.env.NODE_ENV === 'development') {
      console.error(`[ENV ERROR] ${errorMessage}`);
      console.warn(
        '[ENV WARNING] Application may not function correctly without these variables.'
      );
    } else {
      throw new Error(errorMessage);
    }
  }

  return true;
};

/**
 * Environment configuration object
 * Provides typed access to environment variables
 */
export const env = {
  // Auth0 Configuration
  auth0: {
    domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
    clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || '',
    audience: process.env.REACT_APP_AUTH0_AUDIENCE || '',
  },

  // API Configuration
  api: {
    baseUrl: process.env.REACT_APP_SERVER_DOMAIN || '',
  },

  // App Configuration
  app: {
    env: process.env.REACT_APP_ENV || OPTIONAL_ENV_VARS.REACT_APP_ENV,
    isDev: process.env.REACT_APP_ENV === 'dev',
    isProd: process.env.REACT_APP_ENV === 'production',
    isTest: process.env.NODE_ENV === 'test',
  },
};

/**
 * Check if we're in a development environment
 */
export const isDevelopment = () => env.app.isDev || process.env.NODE_ENV === 'development';

/**
 * Check if we're in a production environment
 */
export const isProduction = () => env.app.isProd;

export default env;
